import { Button, Dropdown } from 'antd';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { useContext, useMemo, useRef, useState } from 'react';
import Barchart from 'components/molecules/Barchart';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import {
    calculateContainerHeight,
    formatNumWithKMBAlt,
    getConfig,
    scaleMaxValue,
    scaleMinValue,
} from 'utils/helperMethods';
import DownSvg from 'assets/svg/Down';
import CustomDropdownButton from 'components/atoms/CustomDropdownButton';
import { isEmpty, startCase } from 'lodash';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import useDecompositionChartData from './hooks/useDecompositionChartData';
import { groupBarChartWidth } from 'components/organism/GrowthAnalysis/BrandValueChartContainer/BrandValueChartContainer';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Loader from 'components/molecules/Loader';
import DecompositionWaterfallChart from './DecompositionWaterfallChart';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import useDecompositionWaterfallData from './hooks/useDecompositionWaterfallData';
import { useGrowthMarginBridgesContext } from './GrowthMarginBridgesContext';

const DecompositionChart = () => {
    const chartHeight = calculateContainerHeight(2.3);
    const { selectedBridgesKPI } = useGrowthMarginBridgesContext();
    const { appConfig } = useContext(UserContext);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { defaultAttributeForData, growthBridgesKpiLabelMapping, defaultCurrencySymbol } = profitPoolConfig;
    const { allDimAndAttributeTypeList } = useContext(FilterContextData);
    const containerWidth = useContainerWidth({
        containerId: 'decomposition-chart-infinite-scroll',
    });

    const graphViewContainerWidth = Math.max(16 * groupBarChartWidth, containerWidth) + 300;

    const chartRef = useRef<any>(null);
    const [selectedDecompositionLevel, setSelectedDecompositionLevel] = useState<typeof dim1AttributeList[0] | null>(
        defaultAttributeForData,
    );

    const selectedDecompositionChartType = useMemo(() => {
        if (selectedBridgesKPI === 'mix_impact') {
            return 'waterfall';
        } else {
            return 'bar';
        }
    }, [selectedBridgesKPI]);

    const dim1AttributeList = useMemo(
        () =>
            allDimAndAttributeTypeList?.dimentions[0]?.attribute.map((_item: any) => {
                return {
                    ..._item,
                    key: _item.name,
                    dataKey: _item.name,
                    label: startCase(_item.name),
                    dimension: _item?.dimensionReference,
                };
            }),
        [allDimAndAttributeTypeList],
    );

    const {
        decompositionChartData,
        excelExportData: barExcelExportData,
        isPending,
        isError,
        isSuccess,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        min,
        max,
    } = useDecompositionChartData(selectedDecompositionLevel);

    const minBound = useMemo(() => {
        return min > 0 ? 0 : scaleMinValue(10, min);
    }, [min]);
    const maxBound = useMemo(() => {
        return max > 0 ? max : 0;
    }, [max]);

    const {
        isPending: isWaterfallPending,
        isFetching: isWaterfallFetching,
        isError: isWaterfallError,
        isSuccess: isWaterfallSuccess,
        waterFallChartData: decompositionWaterfallData,
        excelExportData: waterfallExcelExportData,
    } = useDecompositionWaterfallData();

    const excelExportData =
        selectedDecompositionChartType === 'waterfall' ? waterfallExcelExportData : barExcelExportData;

    if (!selectedBridgesKPI) {
        return (
            <div>
                <p className="text-lg font-semibold">{'Decomposition'}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <p>Select a bar from the chart above to view decomposition</p>
                </div>
            </div>
        );
    }

    // Determine loading/error/empty states based on the active chart type
    const isChartLoading = selectedDecompositionChartType === 'waterfall' ? isWaterfallFetching : isPending;
    const isChartError = selectedDecompositionChartType === 'waterfall' ? isWaterfallError : isError;
    const isChartEmpty =
        selectedDecompositionChartType === 'waterfall'
            ? isEmpty(decompositionWaterfallData)
            : isEmpty(decompositionChartData);

    if (isChartLoading) {
        return (
            <div>
                <p className="text-lg font-semibold">{'Decomposition'}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loader />
                </div>
            </div>
        );
    }

    if (isChartError) {
        return (
            <div>
                <p className="text-lg font-semibold">{'Decomposition'}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ErrorBox />
                </div>
            </div>
        );
    }

    if (isChartEmpty) {
        return (
            <div>
                <p className="text-lg font-semibold">{'Decomposition'}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ErrorBox msg={'No Data'} />
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-row items-center justify-between mb-6">
                <div className="flex flex-row gap-x-4">
                    <p className="text-lg font-semibold">{`Decomposition of ${growthBridgesKpiLabelMapping[selectedBridgesKPI]}`}</p>
                    {selectedDecompositionChartType === 'bar' && (
                        <>
                            <p className="text-lg font-semibold -mx-2 justify-center">{'by'}</p>
                            <Dropdown
                                menu={{
                                    items: dim1AttributeList,
                                    onClick: (info) => {
                                        setSelectedDecompositionLevel(
                                            dim1AttributeList.find((_item: any) => _item.key === info.key) || null,
                                        );
                                    },
                                }}
                                trigger={['click']}
                                placement="bottomRight"
                                arrow={true}
                            >
                                <CustomDropdownButton type="default" className="gap-x-2">
                                    <p>{`${startCase(selectedDecompositionLevel?.dataKey)}`}</p>
                                    <DownSvg />
                                </CustomDropdownButton>
                            </Dropdown>
                        </>
                    )}
                </div>

                <div className="flex flex-row gap-x-2 items-center">
                    <div className="flex justify-end items-center">
                        <span className="flex gap-2">
                            <CopyToClipBoard refOfComponent={chartRef} />
                        </span>
                    </div>
                    <div>
                        <DownloadExcel
                            title={`Decomposition of ${growthBridgesKpiLabelMapping[selectedBridgesKPI]} ${
                                selectedDecompositionChartType === 'bar'
                                    ? 'by ' + startCase(selectedDecompositionLevel?.dataKey)
                                    : ''
                            } `}
                            excelData={excelExportData.data}
                            headers={excelExportData.headers}
                        />
                    </div>
                </div>
            </div>

            {/* Chart */}

            <div ref={chartRef}>
                {selectedDecompositionChartType === 'waterfall' ? (
                    <DecompositionWaterfallChart />
                ) : (
                    <>
                        <div className="items-center flex flex-row justify-between my-2 mb-4">
                            <div className="text-sm">
                                <p>{'Total'}</p>
                            </div>
                        </div>
                        <div className="flex mt-2 flex-row">
                            {/* Y axis */}
                            <div className="flex">
                                <Barchart
                                    padding={[0, 0, 15, 0]}
                                    barWidth={40}
                                    xAxisConfig={true}
                                    xAxisLabelFormatter={(val) => {
                                        return val;
                                    }}
                                    dataset={[]}
                                    height={chartHeight}
                                    loading={false}
                                    chartContainer={`decomposition-chart-y-axis`}
                                    fields={['brand_name', 'totalProfit', 'type']}
                                    minBound={minBound}
                                    maxBound={scaleMaxValue(40, maxBound)}
                                    width={60}
                                    colorKey={'type'}
                                    yAxisConfig={true}
                                    YGrid={false}
                                    yAxisLabelFormatter={(val: any) => {
                                        return `${defaultCurrencySymbol}${formatNumWithKMBAlt(val)}`;
                                    }}
                                />
                            </div>

                            {/* Main Chart */}
                            <HorizontalInfiniteScroller
                                id={`decomposition-chart-infinite-scroll`}
                                hasMore={true}
                                scrollThreshold={0.9}
                                toScrollWidth={0}
                                onNext={(width: number) => {
                                    !isFetchingNextPage && fetchNextPage();
                                }}
                            >
                                <div
                                    className="flex flex-1 max-w-full overflow-x-auto"
                                    id={`decomposition-chart-infinite-scroll`}
                                >
                                    <Barchart
                                        padding={[0, 0, 0, 0]}
                                        barWidth={40}
                                        xAxisConfig={true}
                                        xLabelFontSize={12}
                                        xAxisLabelFormatter={(val) => {
                                            return val;
                                        }}
                                        dataset={decompositionChartData}
                                        height={chartHeight}
                                        loading={isFetchingNextPage}
                                        chartContainer={`decomposition-chart-main`}
                                        fields={['attributeDesc', selectedBridgesKPI]}
                                        minBound={minBound}
                                        maxBound={scaleMaxValue(40, maxBound)}
                                        width={Math.max(
                                            graphViewContainerWidth * (decompositionChartData?.length / 10 || 1),
                                            graphViewContainerWidth,
                                        )}
                                        showLoader={false}
                                        colorKey={'colorKey'}
                                        yAxisConfig={true}
                                        tooltipFormatter={(xAxis, val) => {
                                            return {
                                                name: xAxis,
                                                value: `${defaultCurrencySymbol}${formatNumWithKMBAlt(val, 2, 2)}`,
                                            };
                                        }}
                                        intervalFormatter={(val) => {
                                            return `${defaultCurrencySymbol}${formatNumWithKMBAlt(val, 2, 2)}`;
                                        }}
                                    />
                                </div>
                            </HorizontalInfiniteScroller>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default DecompositionChart;
